import { useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { DataManager } from "@/components/DataManager";
import { PageHeader, StatusBadge } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Wrench, CheckCircle2, DollarSign, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const VENDOR_TYPES = ["HVAC / AC Tech", "Plumbing", "Electrical", "Cleanroom Certifier",
                      "Equipment / Lab", "IT / Networking", "General Contractor", "Other"];
const STATUSES = ["open", "scheduled", "in_progress", "completed", "cancelled"];

const fields = [
  { name: "title", label: "Work Order Title", required: true, full: true },
  { name: "vendor_type", label: "Vendor Type", type: "select", options: VENDOR_TYPES, required: true },
  { name: "vendor_name", label: "Vendor / Company" },
  { name: "contact_name", label: "Contact Person" },
  { name: "phone", label: "Phone", mono: true },
  { name: "call_date", label: "Date Called", type: "date", required: true },
  { name: "scheduled_date", label: "Scheduled / Visit Date", type: "date" },
  { name: "status", label: "Status", type: "select", options: STATUSES, default: "open", required: true },
  { name: "cost", label: "Cost (USD)", type: "number" },
  { name: "invoice_number", label: "Invoice / Work Order #", mono: true },
  { name: "issue", label: "Reported Issue", type: "textarea", full: true },
  { name: "resolution", label: "Resolution / Notes", type: "textarea", full: true },
  { name: "attachment", label: "Attach Quote / Invoice / Photo (PDF or image)", type: "file", full: true },
];

const money = (n) => (n === null || n === undefined || n === "" ? "—"
  : `$${(parseFloat(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

export default function WorkOrders() {
  const { isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulking, setBulking] = useState(false);

  const reload = useCallback(() => {
    setSelected(new Set());
    return api.get("/work_orders").then((r) => setItems(r.data)).catch(() => {});
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const openIds = useMemo(() => items.filter((r) => r.status !== "completed").map((r) => r.id), [items]);
  const selectedOpen = useMemo(() => openIds.filter((id) => selected.has(id)), [openIds, selected]);
  const allOpenSelected = openIds.length > 0 && selectedOpen.length === openIds.length;

  const toggle = (id) => setSelected((s) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const toggleAll = () => setSelected(allOpenSelected ? new Set() : new Set(openIds));

  const markComplete = async (row) => {
    setBusyId(row.id);
    try {
      const { data } = await api.post(`/work_orders/${row.id}/mark-complete`);
      if (data.expense_id) toast.success(`Completed — expense ${money(data.cost)} auto-added`);
      else if (data.status === "already_completed") toast.info("Already completed");
      else toast.success("Marked complete");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusyId(null); }
  };

  const bulkComplete = async () => {
    if (selectedOpen.length === 0) return;
    setBulking(true);
    try {
      const { data } = await api.post("/work_orders/bulk-mark-complete", { ids: selectedOpen });
      const okCount = data.results.filter((r) => r.status === "ok").length;
      toast.success(`Completed ${okCount} work order${okCount === 1 ? "" : "s"}${
        data.expenses_created ? ` — ${data.expenses_created} expense(s) totalling ${money(data.total_cost)}` : ""
      }`);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk complete failed"); }
    finally { setBulking(false); }
  };

  const columns = [
    ...(isAdmin ? [{ name: "sel", label: (
        <input type="checkbox" data-testid="wo-select-all" checked={allOpenSelected}
          onChange={toggleAll} disabled={openIds.length === 0}
          className="accent-pink-700 cursor-pointer" />
      ), render: (r) => r.status === "completed" ? (
        <span className="text-slate-300 text-xs">—</span>
      ) : (
        <input type="checkbox" data-testid={`wo-select-${r.id}`}
          checked={selected.has(r.id)} onChange={() => toggle(r.id)}
          className="accent-pink-700 cursor-pointer" />
      ) }] : []),
    { name: "call_date", label: "Called", mono: true },
    { name: "title", label: "Work Order", render: (r) => (
      <div>
        <div className="font-medium text-slate-800">{r.title}</div>
        {r.vendor_name && <div className="text-xs text-slate-400">{r.vendor_name}</div>}
      </div>
    ) },
    { name: "vendor_type", label: "Type" },
    { name: "phone", label: "Phone", mono: true },
    { name: "scheduled_date", label: "Scheduled", mono: true },
    { name: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
    { name: "cost", label: "Cost", render: (r) => (
      <span className="font-mono inline-flex items-center gap-1">
        {money(r.cost)}
        {r.expense_id && (
          <span title="Linked to Pharmacy Expenses" className="text-emerald-600">
            <DollarSign className="w-3.5 h-3.5" />
          </span>
        )}
      </span>
    ) },
    ...(isAdmin ? [{ name: "complete", label: "", render: (r) => (
      r.status === "completed" ? (
        <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
          <CheckCircle2 className="w-3.5 h-3.5" /> Done
        </span>
      ) : (
        <Button data-testid={`workorder-complete-${r.id}`} size="sm" variant="ghost"
          disabled={busyId === r.id} onClick={() => markComplete(r)}
          className="h-8 gap-1 text-emerald-700 hover:bg-emerald-50">
          <CheckCircle2 className="w-3.5 h-3.5" /> Complete
        </Button>
      )
    ) }] : []),
  ];

  return (
    <div>
      <PageHeader testid="workorders-header" title="Work Orders"
        subtitle="Log every vendor call. Marking a WO complete with a cost auto-creates a Pharmacy Expense so it shows up in Cash Flow." />

      {isAdmin && selectedOpen.length > 0 && (
        <div data-testid="wo-bulk-bar"
          className="flex items-center justify-between gap-3 mb-4 p-3 rounded-lg bg-emerald-50 border border-emerald-200">
          <div className="text-sm text-emerald-900 font-medium">
            {selectedOpen.length} work order{selectedOpen.length === 1 ? "" : "s"} selected
          </div>
          <div className="flex items-center gap-2">
            <Button data-testid="wo-bulk-clear" variant="ghost" size="sm"
              onClick={() => setSelected(new Set())} className="h-8 gap-1 text-slate-500">
              <X className="w-3.5 h-3.5" /> Clear
            </Button>
            <Button data-testid="wo-bulk-complete" size="sm" onClick={bulkComplete} disabled={bulking}
              className="bg-emerald-600 hover:bg-emerald-700 gap-1.5 h-8">
              {bulking ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              Complete {selectedOpen.length}
            </Button>
          </div>
        </div>
      )}

      <DataManager testidPrefix="workorder" endpoint="work_orders" fields={fields} columns={columns}
        items={items} reload={reload} emptyIcon={Wrench} emptyTitle="No work orders yet" />
    </div>
  );
}
